import { Component, Input, OnInit, inject, DestroyRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SidenavService } from 'src/app/services/util/sidenav.service';
import { FormUtilService } from 'src/app/services/util/form-util.service';
import { InstancesService } from 'src/app/services/api/instances.service';
import { translate } from '@jsverse/transloco';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import cloneDeep from 'lodash/cloneDeep';
import { ConfirmationDialogComponent } from '../../confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { ErrorDialogComponent } from '../../error-dialog/error-dialog.component';

@Component({
    selector: 'app-add-ssh-key',
    templateUrl: './add-ssh-key.component.html',
    styleUrls: ['./add-ssh-key.component.scss'],
    standalone: false
})
export class AddSshKeyComponent  implements OnInit {
  @Input() routeData;
  @Input() instanceId;
  @Input() viewKeys;
  private destroyRef = inject(DestroyRef);
  formIndex: number;
  occurrences: any[];
  formConfig: any;
  keyForms: FormGroup[] = [];
  editOption = [];
  enableEdit = [];
  previousEditValue: any;
  enableAdd = true;
  isLoading = false;
  isChanged = false;

  constructor(private sidenavService: SidenavService, private formBuilder: FormBuilder, private instanceService: InstancesService,
    private toastrService: CustomToastrService, private dialog: MatDialog, private toastr: ToastrService) {
    
  }

  ngOnInit(): void {
    if (!this.viewKeys && this.routeData?.sshPublicKeys) {
      this.routeData?.sshPublicKeys.forEach(key => {
        this.setFormData(key);
      })
    }
  }

  closeSidenav(): void {
    this.sidenavService.closeRightSidenav(this.isChanged);
  }

  setFormData(key): void {
    this.formIndex = (this.formIndex || this.formIndex === 0) ? this.formIndex + 1 : 0;
    this.createForm();
    this.keyForms[this.formIndex].get('name').setValue(key.name);
    this.keyForms[this.formIndex].get('publicKey').setValue(key.key);
    this.occurrences = this.occurrences && this.occurrences.length !== 0 ?
      [...this.occurrences, this.occurrences[this.occurrences.length - 1] + 1] : [0];
    this.editOption[this.formIndex] = true;
    this.enableEdit[this.formIndex] = false;
  }


  addForm(): void {
    if (this.keyForms?.length === 10) {
      this.toastr.clear();
      setTimeout(() => {this.toastr.error("Only 10 SSH Keys are allowed")}, 100);
      return;
    }
    this.enableAdd = false;
    this.formIndex = (this.formIndex || this.formIndex === 0) ? this.formIndex + 1 : 0;
    this.createForm();
    this.occurrences = this.occurrences && this.occurrences.length !== 0 ?
      [...this.occurrences, this.occurrences[this.occurrences.length - 1] + 1] : [0];

  }
  
  createForm(): void {
    this.formConfig = {
      name: ['', [Validators.required]],
      publicKey: ['', [Validators.required]],
    };
    this.keyForms[this.formIndex] = this.formBuilder.group(this.formConfig);
    this.enableEdit[this.formIndex] = false;
    this.editOption[this.formIndex] = false;
  }

  getForms(): any {
    let failed = false;
    this.keyForms.forEach(form => {
      if (form && !FormUtilService.validateForm(form)) {
        failed = true;
      }
    });
    if (failed) {
      return;
    } else {
      let formList = [];
      this.keyForms.forEach(form => {
        formList = [...formList, form.getRawValue()];
      });
    }
  }

  cancelKey(form, index): void {
    this.enableAdd = true;
    this.keyForms.splice(index, 1);
    this.formIndex = this.formIndex - 1;
    this.occurrences.splice(index, 1);
  }

  saveKey(form, index): void {
    if (this.isLoading) {
      return;
    }
    if (form && !FormUtilService.validateForm(form)) {
      return;
    }
    let sameName = false;
    let count = 0;
    this.keyForms.forEach(key => {
      if (count !== index && key.getRawValue().name === form?.getRawValue()?.name) {
        sameName = true;
      }
      count = count + 1;
    })
    if (sameName) {
      setTimeout(() => this.keyForms[index].get('name').setErrors({nameNotUnique: true}));
      return;
    }
    // confirmation
    this.dialog.open(ConfirmationDialogComponent, {
      panelClass: 'default-dialog-wrapper',
      width: '566px',
      autoFocus: false,
      data: {
        title: translate('INSTANCE_DETAILS.SAVE_TITLE'),
        message: translate('INSTANCE_DETAILS.SAVE_MESSAGE'),
        confirmButtonText: translate('BUTTONS.SAVE')
      }
    })
    .afterClosed()
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(confirm => {
      if (confirm) {
        this.isChanged = true;
        this.isLoading = true;
        const obj = {
          id: this.instanceId,
          routeId: this.routeData?.routeId,
          username: this.routeData?.overview?.username,
          name: form?.getRawValue()?.name,
          publicKey: form?.getRawValue()?.publicKey
        }
        this.instanceService.postSshKey(obj)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(data => {
            this.enableAdd = true;
            this.editOption[index] = true;
            this.formIndex = undefined;
            this.occurrences = [];
            this.keyForms = [];
            this.routeData = data;
            this.routeData?.sshPublicKeys.forEach(key => {
              this.setFormData(key);
            })
            this.isLoading = false;
            setTimeout(() => {
              const title = translate('INSTANCE_DETAILS.SAVE_SSH_KEY');
              const message = translate('INSTANCE_DETAILS.KEYS');
              this.toastrService.successMessage(message, title);
            }, 500)
          },
          (error) => {
            this.isLoading = false;
            const errorMessage = typeof error === 'string' ? error: typeof error?.error === 'string' ? error.error : error;
            this.dialog.open(ErrorDialogComponent, {
              minHeight: '342px',
              width: '630px',
              panelClass: 'flex-dialog',
              data: {
                title: translate('ERROR_DIALOG.SAVE_KEY_FAILED'),
                description: errorMessage
              }
            });
          });
      }
    });
  }

  editOptionClick(form, index): void {
    this.previousEditValue = cloneDeep(this.keyForms);
    this.enableEdit[index] = true;
  }

  deleteKey(form, index): void {
    if (this.isLoading) {
      return;
    }
    // confirmation
    this.dialog.open(ConfirmationDialogComponent, {
      panelClass: 'default-dialog-wrapper',
      width: '566px',
      autoFocus: false,
      data: {
        title: translate('INSTANCE_DETAILS.DELETE_TITLE'),
        message: translate('INSTANCE_DETAILS.DELETE_MESSAGE'),
        confirmButtonText: translate('BUTTONS.DELETE')
      }
    })
    .afterClosed()
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(confirm => {
      if (confirm) {
        this.isChanged = true;
        this.isLoading = true;
        const obj = {
          id: this.instanceId,
          routeId: this.routeData?.routeId,
          username: this.routeData?.overview?.username,
          keyname: form?.getRawValue()?.name
        }
        this.instanceService.deleteSshKey(obj)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(data => {
            this.keyForms.splice(index, 1);
            this.formIndex = this.formIndex - 1;
            this.occurrences.pop();
            this.isLoading = false;
            setTimeout(() => {
              const title = translate('INSTANCE_DETAILS.DELETE_KEY');
              const message = translate('INSTANCE_DETAILS.KEYS');
              this.toastrService.successMessage(message, title);
            }, 500);
          },
          (error) => {
            this.isLoading = false;
           const errorMessage = typeof error === 'string' ? error: typeof error?.error === 'string' ? error.error : error;
            this.dialog.open(ErrorDialogComponent, {
              minHeight: '342px',
              width: '630px',
              panelClass: 'flex-dialog',
              data: {
                title: translate('ERROR_DIALOG.DELETE_KEY_FAILED'),
                description: errorMessage
              }
            });
          });
      }
    });
  }

  editKey(form, index): void {
    if (this.isLoading) {
      return;
    }
    if (form && !FormUtilService.validateForm(form)) {
      return;
    }
    const previous = this.previousEditValue[index].getRawValue();
    if (previous.name === form.getRawValue().name && previous.publicKey === form.getRawValue().publicKey) {
      this.editOption[index] = true;
      this.enableEdit[index] = false;
      return;
    }
    let sameName = false;
    let count = 0;
    this.keyForms.forEach(key => {
      if (count !== index && key.getRawValue().name === form?.getRawValue()?.name) {
        sameName = true;
      }
      count = count + 1;
    })
    if (sameName) {
      setTimeout(() => this.keyForms[index].get('name').setErrors({nameNotUnique: true}));
      return;
    }
    // confirmation
    this.dialog.open(ConfirmationDialogComponent, {
      panelClass: 'default-dialog-wrapper',
      width: '566px',
      autoFocus: false,
      data: {
        title: translate('INSTANCE_DETAILS.SAVE_TITLE'),
        message: translate('INSTANCE_DETAILS.SAVE_MESSAGE'),
        confirmButtonText: translate('BUTTONS.SAVE')
      }
    })
    .afterClosed()
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(confirm => {
      if (confirm) {
        this.isChanged = true;
        this.isLoading = true;
        const obj = {
          id: this.instanceId,
          routeId: this.routeData?.routeId,
          username: this.routeData?.overview?.username,
          keyname : form?.getRawValue()?.name,
          publicKey: form?.getRawValue()?.publicKey
        }
        this.instanceService.putSshKey(obj)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(data => {
            this.editOption[index] = true;
            this.enableEdit[index] = false;
            this.formIndex = undefined;
            this.occurrences = [];
            this.keyForms = [];
            this.routeData = data;
            this.routeData?.sshPublicKeys.forEach(key => {
              this.setFormData(key);
            })
            this.isLoading = false;
            setTimeout(() => {
              const title = translate('INSTANCE_DETAILS.SSH_KEY');
              const message = translate('INSTANCE_DETAILS.SSH_KEY_UPDATED');
              this.toastrService.successMessage(message, title);
            }, 500);
          },
          (error) => {
            this.isLoading = false;
             const errorMessage = typeof error === 'string' ? error: typeof error?.error === 'string' ? error.error : error;
            this.dialog.open(ErrorDialogComponent, {
              minHeight: '342px',
              width: '630px',
              panelClass: 'flex-dialog',
              data: {
                title: translate('ERROR_DIALOG.SAVE_KEY_FAILED'),
                description: errorMessage
              }
            });
          });
      }
    });
  }

  cancelEdit(form, index): void {
    const previous = this.previousEditValue[index].getRawValue();
    this.keyForms[index].get('name').setValue(previous.name);
    this.keyForms[index].get('publicKey').setValue(previous.publicKey);
    this.editOption[index] = true;
    this.enableEdit[index] = false;
  }

  onCopy(field): void {
    this.toastrService.clear();
    this.toastrService.checkboxMessage(field + ' Copied');
  }
}
